import { useEffect, useState, useLayoutEffect } from "react";
import { X, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

// 6-step interactive tooltip tour for first-time visitors on /trade.
// Stored decision in localStorage so it never shows twice.
// v2 — added "How to use" + deposit (Options account!) steps.
const TOUR_KEY = "vts:onboarded:v2";

const STEPS = [
  {
    title: "Welcome to VTS",
    body:
      "VTS is your custom Deriv trading desk. Watch markets, run a smart bot, and trade forex, gold, and crypto — all in one place.",
    target: null, // intro: no highlight, centred
    cta: "Show me around",
  },
  {
    title: "Live Price Chart",
    body:
      "Every Deriv volatility index streams in real-time. Use the timeframe buttons to switch from live ticks to 1m/5m/15m/1h candles.",
    target: '[data-testid="symbol-chart"]',
    cta: "Next",
  },
  {
    title: "Switch tabs to find features",
    body:
      "🚀 Rocket flies Accumulators with a 24/7 server bot. Auto Bot runs the digit bot. Pro Trade is the manual terminal, Signals shows live strategy picks, History lists every trade.",
    target: '[data-testid="desktop-tab-heli"], [data-testid="mobile-tab-heli"]',
    cta: "Next",
  },
  {
    title: "Stuck? Tap 'How to use'",
    body:
      "The full tutorial lives here — how to trade, how every bot works, strategy tips and safety rules. Open it any time you're unsure.",
    target: '[data-testid="open-tutorial-btn"]',
    cta: "Next",
  },
  {
    title: "How to deposit (important!)",
    body:
      "Tap 'Add funds' for the step-by-step guide. Key rule: after depositing on Deriv, TRANSFER the money to your Options (CR…) account — that's the account VTS and the bots trade from. Money left in the Wallet is NOT used.",
    target: '[data-testid="header-add-funds-btn"]',
    cta: "Next",
  },
  {
    title: "Connect a real account",
    body:
      "You're in Demo Mode right now. To trade real Deriv markets, tap 'Connect real account' — it only needs a free API token (30 seconds).",
    target: '[data-testid="header-connect-token-btn"]',
    cta: "Got it",
  },
];

const OnboardingTour = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    // Defer 600ms so the dashboard has time to render the targets before
    // we measure them.
    const t = setTimeout(() => {
      try {
        if (localStorage.getItem(TOUR_KEY) === "1") return;
      } catch {
        /* ignore */
      }
      setOpen(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const s = STEPS[step];
    if (!s?.target) {
      setRect(null);
      return undefined;
    }
    const measure = () => {
      // Try each selector in the comma-separated list, take the first match
      const selectors = s.target.split(",").map((x) => x.trim());
      let el = null;
      for (const sel of selectors) {
        try {
          el = document.querySelector(sel);
          if (el) break;
        } catch {
          /* invalid selector */
        }
      }
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
      // Scroll target into view so the highlight isn't off-screen
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    measure();
    const t = setTimeout(measure, 350); // re-measure after scroll
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [open, step]);

  const dismiss = () => {
    try {
      localStorage.setItem(TOUR_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const next = () => {
    if (step >= STEPS.length - 1) dismiss();
    else setStep(step + 1);
  };

  if (!open) return null;
  const s = STEPS[step];

  // Tooltip position — centered when no rect, otherwise below the target.
  const tooltipStyle = rect
    ? {
        top: Math.min(
          rect.top + rect.height + 16,
          (typeof window !== "undefined" ? window.innerHeight + window.scrollY : 0) - 280,
        ),
        left: Math.min(
          Math.max(rect.left + rect.width / 2 - 180, 16),
          (typeof window !== "undefined" ? window.innerWidth : 360) - 376,
        ),
      }
    : null;

  return (
    <div data-testid="onboarding-tour" className="fixed inset-0 z-[100] pointer-events-none">
      {/* Dimmed backdrop with a cut-out hole around the highlighted target.
          We render four solid divs around the rect so the target stays clickable. */}
      {rect ? (
        <>
          <div
            className="absolute bg-black/70 pointer-events-auto"
            style={{ top: 0, left: 0, right: 0, height: rect.top - 8 }}
            onClick={dismiss}
          />
          <div
            className="absolute bg-black/70 pointer-events-auto"
            style={{ top: rect.top + rect.height + 8, left: 0, right: 0, bottom: 0 }}
            onClick={dismiss}
          />
          <div
            className="absolute bg-black/70 pointer-events-auto"
            style={{ top: rect.top - 8, left: 0, width: rect.left - 8, height: rect.height + 16 }}
            onClick={dismiss}
          />
          <div
            className="absolute bg-black/70 pointer-events-auto"
            style={{
              top: rect.top - 8,
              left: rect.left + rect.width + 8,
              right: 0,
              height: rect.height + 16,
            }}
            onClick={dismiss}
          />
          {/* Highlighted ring around the target */}
          <div
            className="absolute pointer-events-none rounded-lg ring-4 ring-vts-blue ring-offset-2 ring-offset-vts-bg animate-pulse"
            style={{
              top: rect.top - 6,
              left: rect.left - 6,
              width: rect.width + 12,
              height: rect.height + 12,
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0 bg-black/70 pointer-events-auto"
          onClick={dismiss}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute pointer-events-auto w-[360px] max-w-[92vw] bg-vts-surface border border-vts-blue/40 rounded-lg shadow-2xl shadow-vts-blue/20 p-5"
        style={
          tooltipStyle || {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }
        }
      >
        <button
          type="button"
          data-testid="onboarding-skip"
          onClick={dismiss}
          className="absolute top-3 right-3 text-vts-silver/50 hover:text-white transition"
          aria-label="Skip tour"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-vts-blue" />
          <span className="text-[10px] uppercase tracking-wider text-vts-blue font-mono">
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-white text-lg mb-2">
          {s.title}
        </h3>
        <p className="text-sm text-vts-silver/80 leading-relaxed mb-4">
          {s.body}
        </p>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={dismiss}
            className="text-xs text-vts-silver/60 hover:text-white transition"
          >
            Skip tour
          </button>
          <Button
            data-testid="onboarding-next"
            onClick={next}
            className="bg-vts-blue hover:bg-vts-blueHover text-white"
            size="sm"
          >
            {s.cta} <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === step ? "w-6 bg-vts-blue" : "w-1.5 bg-vts-silver/30"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
